import { useCallback, useEffect, useState } from "react";
import { listFootstepAccessLogs } from "./footstepsApi.js";

export const FOOTSTEP_ACCESS_LOG_DEFAULT_PAGE_SIZE = 25;

/**
 * Fetches GET /api/backoffice/tracking/footstep-access-logs - the
 * SuperAdmin-only compliance review list (Prompt 114). Mirrors
 * ../tracking/useLatestTrackingList.js's shape (real server-side
 * pagination/filtering, previous data stays visible during a refetch), with
 * one difference: `enabled` lets the host page defer the first fetch until
 * the Access Logs tab is actually opened, rather than firing it the moment
 * the Footstep History page mounts. A 403 from a non-SuperAdmin actor
 * surfaces as a normal `status: "error"` result - FootstepHistoryPage is
 * responsible for rendering that as a calm restricted state, never a fake
 * empty list.
 *
 * `filters` must be a stable object reference (the caller should build it
 * with useMemo).
 */
export function useFootstepAccessLogs(filters, { enabled = true } = {}) {
  const [state, setState] = useState({ status: "idle", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let cancelled = false;
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));

    listFootstepAccessLogs(filters)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", data: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters, enabled, reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, refetch };
}
